import { Box, Card, Divider, Typography, useTheme } from "@mui/material";
import { Booking } from "lib/features/booking/types";
import { bookingCardStyles } from "styles/BookingReferralDirectory/bookingCard.styles";

import BoatImage from "assets/images/boat.png";
import moment from "moment";
import { useNavigate } from "react-router-dom";

import { formatMoney } from "util/commonHelper";
import calenderSimple from "../../../assets/icons/cali.svg";
import MessageIcon from "../../../assets/icons/envelope_mail.svg";
import MoneyIcon from "../../../assets/icons/money_icon.svg";
import PhoneCallIcon from "../../../assets/icons/phone_call.svg";
import PriceTag from "../../../assets/icons/price_tag_black.svg";
import ClockIcon from "../../../assets/icons/time.svg";

import UserAvatar from "../../../assets/icons/user-n.svg";

const baseUrl = "https://applicationimages.s3.amazonaws.com";

interface BookingCardProps {
  booking: Booking;
}

interface CardDetails {
  image: string;
  title: string;
  price: number;
  businessPay: number;
  businessName: string;
  reservationDate: string;
  bookingEndTime: string;
  purchaseDate: string;
  fee: number;
  comissionPay: number;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
}

const BookingCard = ({ booking }: BookingCardProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  console.log("booking in card: ", booking);
  const getImageUri = () => {
    if (!booking) return BoatImage;

    const businessUserImages = booking?.businessUser?.[0]?.businessImages?.filter((img: { record_status: boolean; }) => img.record_status === true) || [];
    if (businessUserImages.length > 0) {
      return `${baseUrl}/${businessUserImages[0].uri}`;
    }

    if (booking?.referral?.user?.avatar) {
      return `${baseUrl}/${booking?.referral?.user?.avatar}`;
    }

    const activeItemImages = booking?.referral?.item?.itemImages?.filter(img => img.record_status === true) || [];
    if (activeItemImages.length > 0) {
      return `${baseUrl}/${activeItemImages[0].uri}`;
    }

    const activeBusinessImages = booking?.referral?.business?.businessImages?.filter(img => img.record_status === true) || [];
    if (activeBusinessImages.length > 0) {
      return `${baseUrl}/${activeBusinessImages[0].uri}`;
    }

    return BoatImage;
  };

  let card: CardDetails = {
    image: "",
    title: "",
    price: 0,
    businessPay: 0,
    businessName: "",
    reservationDate: "",
    bookingEndTime: "",
    purchaseDate: "",
    comissionPay: 0,
    fee: 0,
    customerName: "",
    customerPhone: "",
    customerEmail: "",
  };

  card.businessPay = booking?.business_payout;
  card.purchaseDate = booking?.created_time;
  card.comissionPay = booking?.commission_payout;
  card.customerName = booking?.customer_name;
  card.customerPhone = booking?.customer_phone;
  card.customerEmail = booking?.customer_email;

  if (booking?.farehabor_booking_details) {
    card.title =
      booking?.farehabor_booking_details?.booking?.availability?.item?.name;
    card.businessName =
      booking?.farehabor_booking_details?.booking?.company?.name;

    card.price = booking?.farehabor_booking_details?.booking?.amount_paid / 100;
    card.reservationDate =
      booking?.farehabor_booking_details?.booking.availability?.start_at;
    card.bookingEndTime =
      booking?.farehabor_booking_details?.booking?.availability?.end_at;
  } else {
    card.title = booking?.referral?.item?.itemDetails[0]?.item_name;
    card.businessName = booking?.referral?.business?.business;

    card.price = booking?.customer_total;
    card.reservationDate = (booking as any)?.reservation_time;
  }

  const businessUserName = booking?.businessUser?.[0]?.business;

  const referrerFirstName = booking?.referral?.user?.first_name?.trim() || "";
  const referrerLastName = booking?.referral?.user?.last_name || "";
  const userFullName = `${referrerFirstName} ${referrerLastName}`.trim();

  const referrerFullName = businessUserName || userFullName || "Admin";

  const duration = booking?.referral?.item?.itemDetails?.[0]?.duration;

  return (
    <Card
      sx={{ ...bookingCardStyles.card(theme) }}
      onClick={() => {
        navigate(`/booking-details/${booking?.id}`);
      }}
    >

      {/* Top section with image */}
      <Box sx={bookingCardStyles.imageBox}>
        <img
          src={getImageUri()}
          alt={"booking-image"}
          style={{ ...bookingCardStyles.image }}
        />
      </Box>

      {/* Details Section */}
      <Box sx={{ ...bookingCardStyles.detailsContainer }}>

        {/* Business Name and Payment Info */}
        <Box sx={bookingCardStyles.commonRow}>
          <Box>
            {card?.title && (
              <Typography sx={bookingCardStyles.title}>{card?.title}</Typography>
            )}
            <Typography component="p" fontSize="17px" fontWeight="400px">
              {referrerFullName || "Admin"}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={3} sx={{ marginLeft: "auto" }}>
            {booking.record_status === false && (
              <Box sx={bookingCardStyles.canceledBanner}>
                <Typography style={bookingCardStyles.canceledText}>
                  Canceled
                </Typography>
              </Box>
            )}

            <Box>

              <Box display="flex" gap="10px" alignItems="center">
                <Typography sx={bookingCardStyles.lightSmallText1}>
                  Purchase on {moment(card?.purchaseDate).format("ddd, MMM D, YYYY")}
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" justifyContent="flex-end" gap={2}>
                <Box
                  sx={[
                    bookingCardStyles.iconContainer,
                  ]}
                >
                  <img src={PriceTag} alt="priceTag-icon" />
                  <Typography style={bookingCardStyles.lightText} sx={{ display: "flex" }}>
                    <Typography sx={bookingCardStyles.darkText}>
                      Price:
                    </Typography> {formatMoney(card?.price)}
                  </Typography>
                </Box>
                <Box sx={bookingCardStyles.iconContainer}>
                  <img src={MoneyIcon} alt="commission-icon" />
                  <Typography
                    sx={[bookingCardStyles.lightText, { color: "#328734" }]}
                  >
                    <span style={{ fontWeight: "bold" }}>Commission:</span> $
                    {formatMoney(card?.comissionPay)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ mt: "8px", mb: "0", backgroundColor: "#8D8D8D" }} />

        <Box display="flex" flexDirection={{ xs: "column", md: "row" }}>
          <Box width="100%" p="8px" borderRight={{ md: "1px solid #8D8D8D" }}>
            <Typography fontSize="17px" fontWeight="500">
              Details
            </Typography>
            <Box sx={[bookingCardStyles.commonRow, { justifyContent: "flex-start" }]}>
              <Box display="flex" gap="10px" alignItems="center">
                <img src={calenderSimple} alt="calender-icon" />
                <Typography sx={bookingCardStyles.darkText}>
                  Reservation Date:
                </Typography>
                <Typography sx={bookingCardStyles.lightSmallText}>
                  {moment(booking?.reservation_time).format("ddd, MMM D, YYYY")}
                </Typography>
              </Box>
            </Box>
            <Box
              display="flex"
              gap="10px"
              alignItems="center"
            >
              <img src={ClockIcon} alt="clock-icon" />
              <Typography sx={[bookingCardStyles.darkText]}>Time</Typography>
              <Typography sx={bookingCardStyles.lightSmallText}>
                {moment(card?.reservationDate).format("h:mm A")}
                {duration && ` • ${duration} hours`}
              </Typography>
            </Box>
          </Box>
          <Box width="100%" p="8px" maxWidth="450px">
            <Typography fontSize="17px" fontWeight="600">
              Customer Info
            </Typography>
            {card?.customerName && (
              <Box sx={bookingCardStyles.commonRow}>
                <Box display="flex" gap="10px" alignItems="center">
                  <img src={UserAvatar} alt="customer-icon" />
                  <Typography sx={bookingCardStyles.lightSmallText}>
                    {card?.customerName}
                  </Typography>
                </Box>
                {card?.customerPhone && (
                  <Box display="flex" gap="10px" alignItems="center" sx={{ ml: { md: "10px" } }}>
                    <img
                      src={PhoneCallIcon}
                      alt="phone-icon"
                    />
                    <Typography sx={bookingCardStyles.lightSmallText}>
                      {card.customerPhone}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            {card?.customerEmail && (
              <Box sx={bookingCardStyles.commonRow}>
                <Box display="flex" gap="10px" alignItems="center">
                  <img src={MessageIcon} alt="email-icon" />
                  <Typography sx={bookingCardStyles.lightSmallText}>
                    {card.customerEmail}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export default BookingCard;